/**
 * Admin -> Settings -> Driver qoidalari (2026-10-18): (1) saqlashda majburiy maydonlar va yo'qotish sababi majburiymi --
 * foydalanuvchi qoidasi: hamma majburiylik sozlanadi; (2) muammoli driverlar -- qaysi driver "e'tibor kerak" hisoblanadi.
 * Backend: apps/drivers/problems.py, DriverWriteSerializer.validate, validate_transition. "Harakat yo'q" kunlari Statuslar
 * bo'limida, chek majburiyligi Budjet bo'limida -- bitta boshqaruv ikki joyda takrorlanmaydi.
 */
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import { PIPELINE_ORDER } from "@/features/drivers/types";
import type { PanelProps } from "./SettingsPage";
import { Card, CardBody, CardHeader } from "@/components/ui/card";
import { Checkbox, Field, NumberInput, Select } from "@/components/ui/field";
import { Toggle } from "@/components/ui/toggle";

/** Backend REQUIRABLE bilan bir xil ro'yxat */
export const REQUIRABLE_FIELDS = [
  "phone", "email", "state", "city", "date_of_birth", "cdl_class", "cdl_state", "cdl_number", "cdl_expires", "medical_card_expires",
  "experience_months", "driver_type", "freight_type", "endorsements", "equipment", "home_time", "desired_pay", "available_from",
] as const;

function FieldPicker({ picked, onChange, t }: { picked: string[]; onChange: (next: string[]) => void; t: (k: string) => string }) {
  return (
    <div className="grid grid-cols-2 gap-x-6 gap-y-1.5 md:grid-cols-3">
      {REQUIRABLE_FIELDS.map((f) => (
        <Checkbox key={f} label={t(`problems.fields.${f}`)} checked={picked.includes(f)} onChange={(e) => onChange(e.target.checked ? [...picked, f] : picked.filter((x) => x !== f))} />
      ))}
    </div>
  );
}

export function ProblemsPanel({ draft, set }: PanelProps) {
  const { t } = useTranslation("settings");
  const { t: tc } = useTranslation();
  return (
    <>
      <Card>
        <CardHeader title={t("problems.formTitle")} subtitle={t("problems.formHint")} />
        <CardBody className="space-y-5">
          <FieldPicker picked={draft.driver_form_required_fields ?? []} onChange={(next) => set("driver_form_required_fields", next)} t={t} />
          <Toggle label={t("problems.lostReason")} hint={t("problems.lostReasonHint")} checked={draft.lost_reason_required ?? true} onChange={(v) => set("lost_reason_required", v)} />
          <p className="text-[12px] text-muted">
            {t("problems.receiptHint")}{" "}
            <Link to="/admin/settings?section=expenses" className="text-primary hover:underline">
              {t("sections.expenses")}
            </Link>
          </p>
        </CardBody>
      </Card>
      <Card>
        <CardHeader title={t("problems.title")} subtitle={t("problems.subtitle")} />
        <CardBody className="space-y-5">
          <div>
            <div className="text-[13px] font-semibold">{t("problems.requiredTitle")}</div>
            <p className="mb-2 text-[12px] text-muted">{t("problems.requiredHint")}</p>
            <FieldPicker picked={draft.driver_required_fields ?? []} onChange={(next) => set("driver_required_fields", next)} t={t} />
          </div>
          <div className="grid gap-4 md:grid-cols-2">
            <Field label={t("problems.fromStatus")} hint={t("problems.fromStatusHint")}>
              <Select value={draft.driver_required_from_status ?? "qualified"} onChange={(e) => set("driver_required_from_status", e.target.value)}>
                {PIPELINE_ORDER.map((s) => (
                  <option key={s} value={s}>
                    {tc(`status.${s}`)}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label={t("problems.expiringDays")} hint={t("problems.expiringHint")}>
              <NumberInput min={1} max={365} value={draft.documents_expiring_days ?? 30} onChange={(e) => set("documents_expiring_days", Number(e.target.value))} />
            </Field>
          </div>
          <p className="text-[12px] text-muted">
            {t("problems.staleHint", { days: draft.pipeline_stale_days })}{" "}
            <Link to="/admin/settings?section=statuses" className="text-primary hover:underline">
              {t("sections.statuses")}
            </Link>
          </p>
        </CardBody>
      </Card>
    </>
  );
}
